import { Injectable } from "@angular/core";
import { OverlayContainer } from "@angular/cdk/overlay";
import { BehaviorSubject } from "rxjs";

export type ThemeMode = "light" | "dark";

@Injectable({ providedIn: "root" })
export class ThemeService {
  private static readonly STORAGE_KEY = "theme";

  private readonly themeSubject = new BehaviorSubject<ThemeMode>("light");
  readonly theme$ = this.themeSubject.asObservable();

  get currentTheme(): ThemeMode {
    return this.themeSubject.value;
  }

  constructor(private overlayContainer: OverlayContainer) {}

  init(): void {
    if (!this.isBrowser()) {
      // On server, just set subject without touching DOM
      this.themeSubject.next("light");
      return;
    }
    const stored = this.safeStorageGet(ThemeService.STORAGE_KEY) as ThemeMode | null;
    const prefersDark = typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-color-scheme: dark)").matches;
    const initial: ThemeMode = stored ?? (prefersDark ? "dark" : "light");
    this.applyTheme(initial);
  }

  toggle(): void {
    const next: ThemeMode = this.currentTheme === "dark" ? "light" : "dark";
    this.applyTheme(next);
  }

  setTheme(mode: ThemeMode): void {
    this.applyTheme(mode);
  }

  private applyTheme(mode: ThemeMode): void {
    this.themeSubject.next(mode);
    this.safeStorageSet(ThemeService.STORAGE_KEY, mode);
    if (!this.isBrowser()) return;
    const root = document.documentElement;
    if (mode === "dark") {
      root.classList.add("dark");
      root.setAttribute("data-theme", "dark");
    } else {
      root.classList.remove("dark");
      root.setAttribute("data-theme", "light");
    }
    // Sync Angular CDK Overlay container (menus, dialogs, etc.)
    try {
      const overlayEl = this.overlayContainer.getContainerElement();
      overlayEl.classList.toggle("dark", mode === "dark");
      overlayEl.setAttribute("data-theme", mode);
    } catch {
      // ignore (likely not in browser)
    }
  }

  private isBrowser(): boolean {
    return typeof window !== "undefined" && typeof document !== "undefined";
  }

  private safeStorageGet(key: string): string | null {
    try {
      if (typeof localStorage === "undefined") return null;
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private safeStorageSet(key: string, value: string): void {
    try {
      if (typeof localStorage === "undefined") return;
      localStorage.setItem(key, value);
    } catch {
      // ignore
    }
  }
}


